/**
 * /login — the auth screen, in login and signup modes.
 *
 * Transcribed from the monolith's `if (!isAuthenticated)` early return, which
 * rendered outside the app shell — no sidebar, no player bar. That is preserved
 * here: this is a public route mounted alongside the shell rather than inside it.
 *
 * ============================================================================
 * SIMULATED. THERE IS NO AUTHENTICATION. Any valid-looking email with a 6+
 * character password signs you in, no account is created, no password is checked
 * against anything, and a reload signs you out. Nothing here is a security
 * boundary. See src/api/authApi.ts and docs/api-contract.md, "Authentication".
 *
 * GOOGLE SIGN-IN IS PRESENT BUT NOT WORKING, AND THE SCREEN SAYS SO. The
 * button is a real control calling the real store action; that action rejects,
 * because there is no provider client id and no backend to exchange an authorization
 * code with. The rejection is shown in the same error line a bad password uses. See
 * components/account/ProviderButtons.tsx for why refusing beats faking.
 * ============================================================================
 *
 * SIGNUP CONTINUES INTO ONBOARDING rather than landing on the Dashboard. A new
 * account has never chosen a plan, so it goes to /onboarding — one screen, skippable
 * — and lands on the Dashboard from there. Logging in is unchanged: an existing
 * account has already been through it.
 *
 * ============================================================================
 * LEGAL CONSENT IS A HARD GATE ON SIGNUP, AND IT IS ENFORCED HERE. No account is
 * created unless the Terms + Privacy checkbox is ticked: `handleSubmit` returns before
 * `signup()` is reached, and the submit button is disabled while it is unticked. Both,
 * rather than either — the disabled button is the affordance and the guard is the rule,
 * and a form that relies only on a disabled button is one Enter keypress from being
 * wrong.
 *
 * ONE AGREEMENT TICK, AND THE MARKETING VALUE IS STILL SENT SEPARATELY. There used to be
 * a second checkbox here for product email; it is gone (Batch 7 §19 — "There should be
 * ONE agreement tick"). What has NOT changed is the request: `ConsentRecord` still carries
 * `acceptedLegal` and `marketingOptIn` as two independent fields, so a backend that needs
 * to distinguish acceptance of the mandatory Terms from a marketing preference still can.
 *
 * `marketingOptIn` is `false` at signup, because nobody was asked — consent that was never
 * given is off. It is not a constant standing in for a control: the user sets it in
 * Settings, where the "Email updates" toggle already lives, and the agreement block on
 * this form says so. See components/account/ConsentFields.tsx.
 *
 * THE CONSENT RECORD IS BUILT AND CANNOT BE FILED. `consentApi.record` rejects, because
 * there is no consent store — see api/consentApi.ts, which explains at length why a
 * resolved promise there would be faking persistence rather than mocking a result. The
 * rejection deliberately does NOT fail the signup: the user did accept the terms, the
 * gate on that is real, and the thing that failed is our ability to keep the receipt.
 * Blocking the account would punish them for our missing backend. The gap is disclosed
 * on the form itself instead.
 * ============================================================================
 */
import { useRef, useState } from 'react';
import { Lock, Mail, User as UserIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ConsentFields from '../components/account/ConsentFields';
import ProviderButtons from '../components/account/ProviderButtons';
import { LegalFooter } from '../components/layout';
import { Btn, Logo } from '../components/ui';
import { TextField } from '../components/forms';
import { TurnstileWidget } from '../components/forms/TurnstileWidget';
import { ApiError } from '../api/mock';
import { PROVIDER_UNCONFIGURED_MESSAGE } from '../api/authApi';
import {
  consentApi,
  ConsentUnavailableError,
  CONSENT_REQUIRED_MESSAGE,
} from '../api/consentApi';
import { useAuthStore } from '../stores/authStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import type { AuthMode, AuthProviderId } from '../types/domain';
import { paths } from './paths';

/**
 * Form field values.
 *
 * Local, not in authStore: these are transient input, and putting them in a store
 * would make every keystroke a global update. authStore's header says the same.
 */
interface Fields {
  name: string;
  email: string;
  password: string;
  confirm: string;
  companyWebsite: string;
}

const EMPTY: Fields = { name: '', email: '', password: '', confirm: '', companyWebsite: '' };

/**
 * The marketing-consent value this form sends.
 *
 * IT IS STILL A SEPARATE FIELD ON THE REQUEST, which is what §19 asks for: the record
 * carries `acceptedLegal` and `marketingOptIn` independently, so a backend that needs to
 * distinguish "agreed to the Terms" from "wants product email" can. Removing the second
 * checkbox did not collapse the two values into one — see recordConsent below.
 *
 * FALSE BECAUSE NOBODY WAS ASKED. There is one tick on this form and it is the legal
 * acknowledgement; no question about marketing is put to the user here, so the only honest
 * value to send is the one that requests nothing. It was already false in practice when the
 * box existed, since it was never pre-ticked and most people left it alone.
 *
 * A NAMED CONSTANT RATHER THAN A LITERAL `false` IN THE CALL, so the field reads as a
 * deliberate value with a reason attached instead of looking like a forgotten argument that
 * someone should wire up to a control. When the user does want product email they turn it on
 * in Settings, where the "Email updates" toggle already lives, and the agreement block on
 * this form says exactly that.
 */
const SIGNUP_MARKETING_OPT_IN = false;

/** The screen's fields carry a leading icon and no separate wrapper margin. */
const ICON_SIZE = 14;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>('login');
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [error, setError] = useState('');
  const signupStartedAt = useRef(Date.now());
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  /*
    CONSENT STATE. One boolean, starting FALSE — §3 requires the legal box not to be
    pre-checked, because a pre-ticked box does not collect consent, it asserts it.

    Local rather than in authStore, for the reason its header gives about the password
    fields: this is transient form input until it is submitted, and the thing that
    outlives the form is the ConsentRecord built from it — which has nowhere to live yet.

    `consentTouched` exists so the required box only turns red after a refused submit. A
    box that is red because the user has not reached it yet is an error message about
    nothing.
  */
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [consentTouched, setConsentTouched] = useState(false);

  const login = useAuthStore((s) => s.login);
  const signup = useAuthStore((s) => s.signup);
  const signInWithProvider = useAuthStore((s) => s.signInWithProvider);
  const pending = useAuthStore((s) => s.pending);
  const showToast = useUiStore((s) => s.showToast);

  const signupMode = mode === 'signup';
  const afterLogin = typeof location.state?.afterLogin === 'string' ? location.state.afterLogin : null;
  const set = (key: keyof Fields) => (value: string) => setFields((f) => ({ ...f, [key]: value }));

  /**
   * Signs in and lands on the app.
   *
   * `replace` so Back does not return to the login screen from inside the app —
   * the prototype's goToRoot() reset its history stack for the same reason.
   *
   * `to` is the Dashboard for everything except a fresh signup, which goes to
   * onboarding first. Passed as an argument rather than branched on inside, so the
   * two call sites each say plainly where they are going.
   */
  function enter(
    message: string,
    to: string = paths.dashboard,
    state?: Record<string, unknown>
  ) {
    navigate(to, { replace: true, ...(state ? { state } : {}) });
    showToast(message);
  }

  /**
   * Carries the acceptance to the consent boundary, and swallows its refusal.
   *
   * NOT AWAITED FOR SUCCESS, because there is no success to await: `consentApi.record`
   * always rejects in this build. What this does achieve is real — the record is built
   * with the timestamp and the document versions the user was actually shown, and the
   * call site exists, so when a consent endpoint appears this function's body becomes an
   * await and nothing else here changes.
   *
   * THE REJECTION MUST NOT FAIL THE SIGNUP. The user accepted the Terms; the gate on
   * that is enforced above and is genuine. What failed is our filing, which is our
   * problem, and refusing the account over it would punish them for our incompleteness.
   * So `ConsentUnavailableError` is logged and swallowed, and the missing store is
   * disclosed on the form itself rather than as an error the user cannot act on. Any
   * OTHER error is re-thrown: a real failure from a real endpoint is a different event
   * and should not be hidden by the handler written for this one.
   */
  async function recordConsent() {
    try {
      // `new Date()` here rather than inside buildConsentRecord: a timestamp a function
      // invents is untestable, and this is the field that makes the record evidence.
      // The two values go over separately, as §19 requires — `marketingOptIn` is not
      // derived from the tick. See SIGNUP_MARKETING_OPT_IN for why it is false here.
      await consentApi.record(
        { acceptedLegal: true, marketingOptIn: SIGNUP_MARKETING_OPT_IN },
        new Date()
      );
    } catch (err) {
      if (!(err instanceof ConsentUnavailableError)) throw err;
      // Not shown to the user as an error — see above. Logged so the gap is visible to
      // anyone with a console open, alongside the record that would have been filed.
      console.warn(err.message, err.record);
    }
  }

  async function handleSubmit() {
    setError('');

    // Confirm-password is a client-only concern: there is one password field on
    // the wire, so the API has no way to check the two match.
    if (signupMode && fields.confirm !== fields.password) {
      setError("Passwords don't match");
      return;
    }

    /*
      THE LEGAL GATE. Returns before `signup()` is reached, so no account is created
      without the acknowledgement — which is §3's requirement, and the reason this is a
      guard rather than only a disabled button: a disabled button is an affordance, and
      an affordance is not a rule.

      Checked after the password comparison and before anything else, so a user who has
      got both wrong is told about the password first — the field they were last in.
    */
    if (signupMode && !acceptedLegal) {
      setConsentTouched(true);
      setError(CONSENT_REQUIRED_MESSAGE);
      return;
    }

    try {
      if (signupMode) {
        await signup({
          name: fields.name,
          email: fields.email,
          password: fields.password,
          companyWebsite: fields.companyWebsite,
          signupElapsedMs: Math.max(0, Date.now() - signupStartedAt.current),
          turnstileToken,
        });
        // After the account, not before: consent is recorded for an account, and a
        // record filed against a signup that then failed validation would be a record
        // of an acceptance by nobody.
        await recordConsent();
        const { appAccess, user } = useAuthStore.getState();
        enter(
          `Welcome to Lyrixsa AI, ${fields.name.trim().split(' ')[0]}!`,
          user?.emailVerifiedAt ? (appAccess === 'granted' ? paths.onboarding : paths.upgrade) : paths.verifyEmailPending,
          { emailJustSent: true, email: fields.email }
        );
      } else {
        await login({ email: fields.email, password: fields.password });
        enter(
          'Welcome back!',
          afterLogin ?? (useAuthStore.getState().appAccess === 'granted' ? paths.dashboard : paths.upgrade)
        );
      }
    } catch (err) {
      if (signupMode) {
        setTurnstileToken('');
        setTurnstileResetKey((value) => value + 1);
      }
      if (err instanceof ApiError && err.code === 'email_verification_required') {
        navigate(paths.verifyEmailPending, { state: { email: fields.email } });
        return;
      }
      // Validation messages come from authApi so the rules live in one place
      // instead of being duplicated between the form and the API.
      setError(err instanceof ApiError ? err.message : 'Could not sign you in');
    }
  }

  /**
   * Starts sign-in with Google.
   *
   * EXPECTED TO FAIL, and the failure is the honest outcome rather than a bug to work
   * around — `signInWithProvider` rejects with a 501 because no provider is configured
   * and no backend exists to exchange an authorization code. The message is shown in
   * the form's own error line so it is impossible to miss, and it names what is
   * missing. Nothing about the session changes.
   *
   * If it ever resolves — which it will once a client id and a backend exist — the
   * user is signed in and lands exactly where the email path lands.
   */
  async function handleProvider(provider: AuthProviderId) {
    setError('');
    try {
      await signInWithProvider(provider);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : PROVIDER_UNCONFIGURED_MESSAGE);
    }
  }

  return (
    <div
      style={{
        fontFamily: 'Inter, sans-serif',
        background: c.bg,
        color: c.text1,
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        {/*
          The brand, from the one component that draws it. This was a hand-built 30px copy of
          the sidebar's lockup, and the login screen is the surface where a mark that is
          slightly wrong is most visible: it is the first thing a new account sees, and it is
          the only thing on the page identifying whose login this is.
        */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Logo size={30} />
        </div>

        {/* A real <form> so Enter submits, which the prototype's div did not. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: '24px 22px',
          }}
        >
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 18,
              margin: '0 0 4px',
              textAlign: 'center',
              color: c.text1,
            }}
          >
            {signupMode ? 'Create your account' : 'Welcome back'}
          </h1>
          <p style={{ fontSize: 12, color: c.text3, margin: '0 0 20px', textAlign: 'center' }}>
            {signupMode ? 'Start turning prompts into songs' : 'Log in to your Lyrixsa AI dashboard'}
          </p>

          {signupMode && (
            <>
              <TextField
                label="Full name"
                value={fields.name}
                onChange={set('name')}
                placeholder="e.g. Alex Johnson"
                icon={<UserIcon size={ICON_SIZE} color={c.text3} />}
                marginBottom={12}
              />
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                }}
              >
                <label htmlFor="company-website">Company website</label>
                <input
                  id="company-website"
                  name="companyWebsite"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={fields.companyWebsite}
                  onChange={(event) => set('companyWebsite')(event.target.value)}
                />
              </div>
            </>
          )}

          <TextField
            label="Email"
            type="email"
            value={fields.email}
            onChange={set('email')}
            placeholder="you@example.com"
            icon={<Mail size={ICON_SIZE} color={c.text3} />}
            marginBottom={12}
          />

          <TextField
            label="Password"
            type="password"
            value={fields.password}
            onChange={set('password')}
            placeholder={signupMode ? 'At least 10 characters' : 'Your password'}
            icon={<Lock size={ICON_SIZE} color={c.text3} />}
            marginBottom={signupMode ? 12 : 6}
          />

          {signupMode && (
            <TextField
              label="Confirm password"
              type="password"
              value={fields.confirm}
              onChange={set('confirm')}
              placeholder="Re-enter your password"
              icon={<Lock size={ICON_SIZE} color={c.text3} />}
              marginBottom={6}
            />
          )}

          {/* THE CONSENT BLOCK — signup only, because there is nothing to accept when
              logging back into an account that already accepted it. Above the error line
              so a refusal appears directly beneath the control it is about. */}
          {signupMode && (
            <ConsentFields
              acceptedLegal={acceptedLegal}
              onAcceptedLegalChange={(value) => {
                setAcceptedLegal(value);
                // Clears the refusal the moment it stops applying, rather than leaving a
                // red box and an error above a checkbox the user has just ticked.
                if (value) {
                  setConsentTouched(false);
                  if (error === CONSENT_REQUIRED_MESSAGE) setError('');
                }
              }}
              disabled={pending}
              invalid={consentTouched && !acceptedLegal}
            />
          )}

          {signupMode && (
            <TurnstileWidget
              action="signup"
              resetKey={turnstileResetKey}
              onTokenChange={setTurnstileToken}
            />
          )}

          {/* aria-live so a screen reader announces the failure; the prototype's
              error text appeared silently. */}
          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '6px 0 0' }}>
              {error}
            </p>
          )}

          {/*
            NOT DISABLED WHILE THE BOX IS UNTICKED, and that is a deliberate choice rather
            than a weaker gate. Two reasons, and the second is the one that decided it:

            1. A DISABLED BUTTON EXPLAINS NOTHING. Someone who scrolled past the checkbox
               finds a dead Create account button and no statement of what is wrong with
               it. Pressing it and being told "please accept the Terms of Service and
               Privacy Policy" — in a live region, next to a box that turns red — is the
               route forward the disabled state cannot offer.
            2. IT WOULD MAKE THE ACTUAL ENFORCEMENT UNREACHABLE. Browsers block implicit
               submission when the default button is disabled, so the guard in
               handleSubmit — the thing that genuinely refuses — would only ever be
               entered by a test. An enforcement no real interaction can reach is an
               enforcement nobody has verified.

            THE ACCOUNT STILL CANNOT BE CREATED. `handleSubmit` returns before `signup()`
            for want of the acknowledgement — that guard is the enforcement, and it runs
            on every path into this form including the Enter key. `pending` remains the
            only condition here, exactly as before, and no consent state appears in it.
          */}
          <Btn
            full
            type="submit"
            disabled={pending || (signupMode && !turnstileToken)}
            style={{ marginTop: 14, marginBottom: 10 }}
          >
            {signupMode ? 'Create account' : 'Log in'}
          </Btn>

          {!signupMode && (
            <button
              type="button"
              onClick={() => navigate(paths.resetPassword)}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                marginBottom: 10,
                fontSize: 12,
                color: c.accent,
                cursor: 'pointer',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Forgot password?
            </button>
          )}

          {/* Below the email form, not above it: email is the path that works, and a
              provider button at the top of the card would present the two as equals.
              It rejects in this build — see the header, and ProviderButtons. */}
          <ProviderButtons
            onSelect={(p) => void handleProvider(p)}
            disabled={pending}
          />

          <p style={{ fontSize: 12, color: c.text3, textAlign: 'center', margin: '16px 0 0' }}>
            {signupMode ? 'Already have an account?' : 'New to Lyrixsa AI?'}{' '}
            <button
              type="button"
              onClick={() => {
                if (!signupMode) signupStartedAt.current = Date.now();
                setMode(signupMode ? 'login' : 'signup');
                setError('');
                // Consent resets on a mode switch. Leaving `acceptedLegal` true across a
                // trip to the login form and back would carry an acknowledgement through
                // a screen that never showed the checkbox — the box would come back
                // pre-ticked, which is exactly what §3 forbids.
                setAcceptedLegal(false);
                setConsentTouched(false);
                setTurnstileToken('');
                setTurnstileResetKey((value) => value + 1);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: c.accent,
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
                fontWeight: 500,
              }}
            >
              {signupMode ? 'Log in' : 'Sign up'}
            </button>
          </p>
        </form>

        {/*
          THE FOUR DOCUMENTS, REACHABLE FROM THE AUTH SCREEN IN BOTH MODES.

          §4 asks for these to be linked from signup/auth. Signup has them inside the
          consent checkbox already — those are the two being agreed to and they belong
          exactly there — but login mode shows no checkbox, so without this row a
          returning user has no route to the documents at all. Outside the card rather
          than inside it, so it reads as page furniture rather than part of the form.
        */}
        <LegalFooter marginTop={20} centered />
      </div>
    </div>
  );
}
